import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, size, replace, isNull, isUndefined } from "lodash";
import Box from "@mui/material/Box";

import customerItemService from "@plannel/services/data/customer-item-service";
import customerService from "@plannel/services/data/customer-service";
import DataState from "@plannel/components/aggrid/DataState";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import PaginationContainer from "@plannel/components/PaginationContainer";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ResponseUtil from "../utils/response-util";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import GridDatePicker from "@plannel/components/GridDatePicker";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { httpStatusCode, responseCode } = ResponseUtil;
const { dpABCConfig } = CodeUtil;
const CustomerItem = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);

  const [tPages, setTotalPages] = useState(0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [customerItems, setCustomerItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const currencyMap = useRef({});
  const EXCEL_EXPORT = "excelExport";
  const userData = JSON.parse(localStorage.getItem("user"));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const customerFormatter = (params) => {
    const filterVal = customers.find(arr => arr.customerCd === params.value);
    return filterVal ? filterVal.name : null;
  };

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.customerCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }

    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, ""); 
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const columnDefs = [
    {
      headerName: "customerCd",
      field: "customerCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "customerName",
      field: "customerName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: {
        values: customers.map(c => c.name)
      },
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: customerFormatter
      },
      comparator: (val1, val2) => val1.localeCompare(val2),
      valueSetter: (params) => {
        const selectedName = params.newValue;
        const customer = customers.find(c => c.name === selectedName);
        if (customer) {
          params.data.customerName = customer.name;
          params.data.customerCd = customer.customerCd;
        } else {
          params.data.customerName = null;
          params.data.customerCd = null;
        }
        return true;
      },
      minWidth: 150,
      flex: 2,
      filterType: 'string'
    },
    { headerName: "itemCd", field: "itemCd", minWidth: 150, flex: 2, cellClass: 'stringType', filterType: 'string' },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], minWidth: 300, flex: 5, filterType: 'string' },
    {
      headerName: "invClassCd", field: "invClassCd", minWidth: 60, width: 100,
      editable: false, cellClass: 'stringType', filterType: 'string', filterOption: dpABCConfig, cellRenderer: AbcXyzChip
    },
    { headerName: "unitPrice", field: "unitPrice", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 150, filterType: 'number' },
    { headerName: "salesUnitCost", field: "salesUnitCost", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 150, filterType: 'number' },
    { headerName: "remainExpDays", field: "remainExpDays", type: ["numberColumn", "rightAligned"], width: 150, filterType: 'number' },
    { headerName: "endOfSalesDt", field: "endOfSalesDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    // v26.10: Channel Planning 미출시 — 열만 내린다. 행 데이터엔 값이 남아 저장해도 DB 값이 보존된다
    ...(CodeUtil.SHOW_CHANNEL ? [{ headerName: "targetInvDays", field: "targetInvDays", type: ["numberColumn", "rightAligned"], width: 150, filterType: 'number' }] : []),
    {
      headerName: "demandPlan", field: "dpFlg", type: ["booleanColumn"], width: 100,
      // cellStyle: { backgroundColor: grey[300] },
      // editable: false
      filterType: 'boolean'
    },
    {
      headerName: "baselineForecast", field: "bfFlg", type: ["booleanColumn"], width: 100,
      // cellStyle: { backgroundColor: grey[300] },
      // editable: false
      filterType: 'boolean'
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], minWidth: 80, flex: 1.2, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch?.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveCustomerItems = () => {
    const params = getRequestParams();
    customerItemService.getAll(params)
      .then(response => {
        const { results, totalPages, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setCustomerItems(results);
        setTotalPages(totalPages);
        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridRef.current?.api);

        const reduxState = reduxUtil.createObjectState(viewName, { customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  };

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        const { results } = response.data;
        /*          .filter((result)=>{
                  return result.activeFlg === true && result.delFlg === false;
                });*/
        setCustomers(results);
      })
      .catch(e => {
        debug(e);
      })
  };

  const onGridReady = params => {
    retrieveCustomers();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const removeCustomerItem = () => {
    const customerItemIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        customerItemIds.push(data.id);
      }
    );

    if (customerItemIds.length > 0) {
      customerItemService.remove(customerItemIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveCustomerItems();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveCustomerItem = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      customerItemService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .then(() => {
          retrieveCustomerItems();
        })
        .catch(e => {
          const status = e.response?.status;
          const errorDatas = e.response?.data;

          if (status === httpStatusCode.BadRequest) {
            for (const errorCode of Object.keys(errorDatas)) {
              if (errorCode === responseCode.NOT_FOUND_ITEM) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
              } else if (errorCode === responseCode.INVALID_ITEM_TYPE) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ItemTypeCheck", { itemCodes: errorDatas[errorCode] }) });
              } else {
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
              }
              break;
            }
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const addRow = () => {
    const newRow = {
      id: null,
      customerCd: "",
      activeFlg: true,
      dpFlg: true,
      bfFlg: true
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveCustomerItem();
    } else if (action === "DELETE") {
      removeCustomerItem();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveCustomerItems();
    }
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveCustomerItems();
    }

    return () => {
      setCustomerItems([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(customers)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }

    return () => {
      setCustomerItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} sx={{ mr: 1.5 }} extraCodesRef={extraCustomerCodesRef} />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={customerItemService} columnDefs={columnDefs}
            params={getRequestParams()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={customerItems}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(CustomerItem);